import { Suspense } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from 'react-router-dom';

import { Header } from '@modules/core/components';
import { isUserAdministrator } from '@modules/auth/utils/userRole';
import { LoaderLogin } from '@base-app';
import { MainLayout } from '@common/wrappers';
import { PATHS } from '@common/constants/routes';
import { Permissions } from '@/modules/permissions/types';
import { useAuth } from '@modules/auth/hooks';
import { useAuthStore } from '@modules/auth/store';

import AddTeamMemberScreen from '@modules/teams/views/AddTeamMemberScreen/AddTeamMemberScreen';
import CommunityOutreachScreen from '@/modules/community-outreach/views/CommunityOutreachScreen';
import WithdrawalScreen from '@/modules/withdrawal/views/WithdrawalScreen';
import Completed from '@/modules/genetic-counseling/components/Automated/Completed/Completed';
import Declined from '@/modules/genetic-counseling/components/Automated/Declined/Declined';
import GeneticCounselingScreen from '@/modules/genetic-counseling/views/GeneticCounselingScreen/GeneticCounselingScreen';
import MyAccountScreen from '@modules/my-account/views/MyAccountScreen/MyAccountScreen';
import PatientProfileScreen from '@/modules/genetic-counseling/views/PatientProfileScreen/PatientProfileScreen';
import PrivateRoutes from '../PrivateRoutes/PrivateRoutes';
import Referrals from '@/modules/genetic-counseling/components/Automated/Referrals/Referrals';
import StudyInsightsScreen from '@modules/study-insights/views/StudyInsightsScreen/StudyInsightsScreen';
import TeamsListScreen from '@modules/teams/views/TeamsListScreen/TeamsListScreen';
import Upcoming from '@/modules/genetic-counseling/components/Automated/Upcoming/Upcoming';
import CreatePatientReferralScreen from '@/modules/genetic-counseling/views/CreatePatientReferralScreen/CreatePatientReferralScreen';
import { isSSOUser } from '@/modules/core/utils';
import useOrganizationName from '@/modules/my-account/hooks/useOrganizationName';
import NotFoundScreen from '@/modules/core/views/NotFoundScreen';
import RequirePermission from '@modules/permissions/components/RequirePermission/RequirePermission';
import LoginCallbackRoute from '../LoginCallbackRoute/LoginCallbackRoute';
import { useFeatureFlags } from '@/modules/feature-flags/hooks';
import { FeatureFlags } from '@/modules/feature-flags/types';
import LayoutOne from '@/modules/study-insights/components/ParticipantRecluitment/Layouts/LayoutOne/LayoutOne';
import LayoutThree from '@/modules/study-insights/components/ParticipantRecluitment/Layouts/LayoutThree/LayoutThree';
import CollectionAndSequencing from '@/modules/study-insights/components/CollectionAndSequencing/CollectionAndSequencing';
import SampleTracking from '@/modules/study-insights/components/SampleTracking/SampleTracking';
import ResultsTracking from '@/modules/study-insights/components/ResultsTracking/ResultsTracking';
import { useAnalytics } from '@/modules/analytics/hooks/useAnalytics';

const AppRouter: React.FC<{}> = () => {
  const { user: currentUser } = useAuthStore();
  const { isAuthenticated } = useAuth();
  const organizationName = useOrganizationName();
  const { isFeatureEnabled } = useFeatureFlags();
  useAnalytics();

  // Load routes once user is authenticated.
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path={PATHS.AUTH_CALLBACK} element={<LoginCallbackRoute />} />
        <Route element={<PrivateRoutes />}>
          {/** Base App Routes */}
          <Route
            path={PATHS.TEAMS}
            element={
              <Suspense fallback={<LoaderLogin />}>
                {isAuthenticated === true && <Header />}
                <TeamsListScreen />
              </Suspense>
            }
          />

          {currentUser !== null &&
          isUserAdministrator(currentUser) &&
          organizationName &&
          !isSSOUser(currentUser, organizationName) ? (
            <Route
              path={PATHS.TEAMS_ADD_TEAM_MEMBER}
              element={
                <Suspense fallback={<LoaderLogin />}>
                  {isAuthenticated === true && <Header />}
                  <AddTeamMemberScreen />
                </Suspense>
              }
            />
          ) : (
            <Route path="*" element={<Navigate to={PATHS.ROOT} replace />} />
          )}

          <Route
            path={PATHS.MY_ACCOUNT}
            element={
              <Suspense fallback={<LoaderLogin />}>
                {isAuthenticated === true && <Header />}
                <MyAccountScreen />
              </Suspense>
            }
          />

          {/** End of Base App Routes */}

          <Route
            element={
              <Suspense fallback={<LoaderLogin />}>
                <MainLayout hasPrimarySidebar={true} hasPrimaryHeader={true} />
              </Suspense>
            }
          >
            <Route path={PATHS.ROOT} element={<Navigate to={PATHS.STUDY_INSIGHTS} replace />} />
            <Route
              path={PATHS.STUDY_INSIGHTS}
              element={
                <Suspense fallback={<LoaderLogin />}>
                  <StudyInsightsScreen />
                </Suspense>
              }
            >
              {isFeatureEnabled(FeatureFlags.PARTICIPANT_RECRUITMENT) && (
                <Route
                  path={PATHS.STUDY_INSIGHTS_PAGES.PARTICIPANT_RECRUITMENT}
                  element={
                    <Suspense fallback={<LoaderLogin />}>
                      <RequirePermission to={Permissions.STUDY_INSIGHTS__RECRUITMENT_LAYOUT_1}>
                        <LayoutOne />
                      </RequirePermission>
                      <RequirePermission to={Permissions.STUDY_INSIGHTS__RECRUITMENT_LAYOUT_3}>
                        <LayoutThree />
                      </RequirePermission>
                    </Suspense>
                  }
                />
              )}
              <Route
                path={PATHS.STUDY_INSIGHTS_PAGES.COLLECTION_AND_SEQUENCING}
                element={
                  <Suspense fallback={<LoaderLogin />}>
                    <CollectionAndSequencing />
                  </Suspense>
                }
              />
              <Route
                path={PATHS.STUDY_INSIGHTS_PAGES.SAMPLE_TRACKING}
                element={
                  <Suspense fallback={<LoaderLogin />}>
                    <RequirePermission to={Permissions.CAN_VIEW_PHI}>
                      <SampleTracking />
                    </RequirePermission>
                  </Suspense>
                }
              />
              <Route
                path={PATHS.STUDY_INSIGHTS_PAGES.DELIVERY_AND_RESULTS}
                element={
                  <Suspense fallback={<LoaderLogin />}>
                    <ResultsTracking />
                  </Suspense>
                }
              />
            </Route>
            <Route
              path={PATHS.COMMUNITY_OUTREACH}
              element={
                <RequirePermission to={Permissions.COMMUNITY_OUTREACH} redirect>
                  <Suspense fallback={<LoaderLogin />}>
                    <CommunityOutreachScreen />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path={PATHS.HRN_WITHDRAWAL}
              element={
                <Suspense fallback={<LoaderLogin />}>
                  <WithdrawalScreen />
                </Suspense>
              }
            />
            <>
              <Route
                path={PATHS.GENETIC_COUNSELING.ROOT}
                element={<Navigate to={PATHS.GENETIC_COUNSELING.REFERRALS} replace />}
              />
              <Route
                path={PATHS.GENETIC_COUNSELING.ROOT}
                element={
                  <RequirePermission to={Permissions.GENETIC_COUNSELING} redirect>
                    <Suspense fallback={<LoaderLogin />}>
                      <GeneticCounselingScreen />
                    </Suspense>
                  </RequirePermission>
                }
              >
                <Route
                  path={PATHS.GENETIC_COUNSELING.COMPLETED}
                  element={
                    <Suspense fallback={<LoaderLogin />}>
                      <Completed />
                    </Suspense>
                  }
                />
                <Route
                  path={PATHS.GENETIC_COUNSELING.DECLINED}
                  element={
                    <Suspense fallback={<LoaderLogin />}>
                      <Declined />
                    </Suspense>
                  }
                />
                <Route
                  path={PATHS.GENETIC_COUNSELING.REFERRALS}
                  element={
                    <Suspense fallback={<LoaderLogin />}>
                      <Referrals />
                    </Suspense>
                  }
                />
                <Route
                  path={PATHS.GENETIC_COUNSELING.UPCOMING}
                  element={
                    <Suspense fallback={<LoaderLogin />}>
                      <Upcoming />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path={`${PATHS.GENETIC_COUNSELING.ROOT}/${PATHS.GENETIC_COUNSELING.PATIENT}/:id?`}
                element={
                  <RequirePermission to={Permissions.GENETIC_COUNSELING} redirect>
                    <Suspense fallback={<LoaderLogin />}>
                      <PatientProfileScreen />
                    </Suspense>
                  </RequirePermission>
                }
              />
              <Route
                path={`${PATHS.GENETIC_COUNSELING.ROOT}/${PATHS.GENETIC_COUNSELING.REFERRALS}/${PATHS.GENETIC_COUNSELING.NEW}`}
                element={
                  <RequirePermission to={Permissions.GENETIC_COUNSELING} redirect>
                    <RequirePermission to={Permissions.GENETIC_COUNSELING__CREATE} redirect>
                      <Suspense fallback={<LoaderLogin />}>
                        <CreatePatientReferralScreen />
                      </Suspense>
                    </RequirePermission>
                  </RequirePermission>
                }
              />
            </>
          </Route>
        </Route>
        <Route path="*" element={<NotFoundScreen />} />
      </>,
    ),
  );
  return <RouterProvider router={router} />;
};

export default AppRouter;
